/* eslint-disable */
import React, { useEffect, useRef } from "react";
import "./Landing.css";

// ── Externe links: vul hier je echte URL's in ──────────────────
const DISCORD_URL   = "https://discord.gg/68VMeHvvz"
const RS_SCANNER_URL = "/scan";
const PNF_URL = "/pnf/";
/**
 * Landing — openbare voordeur van Wyckoff Café.
 * onEnter() wordt aangeroepen als de bezoeker naar het dashboard wil
 * (App schakelt dan door naar de LoginPage).
 *
 * Zet skyline_bw_animation.gif en wyckoffeelogo_socials.jpg in /public,
 * dan kloppen de paden hieronder (ook op Vercel).
 */
export default function Landing({ onEnter }) {
  const chartRef = useRef(null);

  // Teken het schema wanneer het in beeld scrolt
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            chart.querySelectorAll(".draw, .fade").forEach((el) => el.classList.add("play"));
            io.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(chart);
    return () => io.disconnect();
  }, []);

  return (
    <div className="lp">
      <header className="lp-header">
        <div className="lp-wrap lp-bar">
          <div className="lp-brand">
            <img className="lp-logo" src="/wyckoffeelogo_socials.jpg" alt="Wyckoff Café logo" />
            Wyckoff&nbsp;Café
          </div>
          <nav className="lp-nav">
            <a href="#basics">The basics</a>
            <a href="#cycle">The cycle</a>
            <a href="#tools">Tools</a>
            <a href="#learn">Learn</a>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="lp-hero">
        <div className="lp-hero-media">
          <img src="/skyline_bw_animation.gif" alt="Candlesticks rising and falling like a city skyline" />
        </div>
        <div className="lp-hero-copy">
          <div className="lp-inner">
            <span className="lp-eyebrow">☕ Price · Volume · The Composite Man</span>
            <h1>Trade in step with the <em>Composite&nbsp;Man</em>.</h1>
            <p className="lp-lede">
              Wyckoff Café teaches you to read a chart the way Richard Wyckoff did —
              through supply, demand, and the footprints large operators leave behind.
              Start with the basics, then pull up a chair in the room.
            </p>
            <div className="lp-cta">
              <a className="lp-btn lp-btn-primary" href={DISCORD_URL} target="_blank" rel="noopener noreferrer">Join the Discord</a>
              <a className="lp-btn lp-btn-ghost" href="#tools">Explore the tools</a>
            </div>
          </div>
        </div>
      </section>

      {/* THE BASICS */}
      <section className="lp-section" id="basics">
        <div className="lp-wrap">
          <div className="lp-sec-head">
            <span className="lp-eyebrow">The basics</span>
            <h2>Three laws that move the market</h2>
            <p>Wyckoff reduced price behaviour to three principles. They sound simple,
               but together they explain almost every move on the chart.</p>
          </div>
          <div className="lp-laws">
            <div className="lp-law">
              <span className="lp-n">Law 01</span>
              <h3>Supply &amp; demand</h3>
              <p>More demand than supply pushes price up; more supply than demand pushes
                 it down. Everything starts here — the rest is timing.</p>
            </div>
            <div className="lp-law">
              <span className="lp-n">Law 02</span>
              <h3>Cause &amp; effect</h3>
              <p>A sideways range is the cause; the breakout from it is the effect. The
                 longer the build-up, the larger the move that can come out of it.</p>
            </div>
            <div className="lp-law">
              <span className="lp-n">Law 03</span>
              <h3>Effort vs. result</h3>
              <p>Volume is the effort, price movement the result. When they fall out of
                 step — heavy volume, little movement — something is turning underneath.</p>
            </div>
          </div>
        </div>
      </section>

      {/* THE CYCLE + schematic */}
      <section className="lp-section" id="cycle">
        <div className="lp-wrap">
          <div className="lp-sec-head">
            <span className="lp-eyebrow">The market cycle</span>
            <h2>Every market breathes in four phases</h2>
            <p>Accumulation, markup, distribution and markdown follow one another, over
               and over. Recognise where you are and you know what tends to come next.</p>
          </div>

          <div className="lp-cycle-grid">
            <div className="lp-cycle">
              <div className="lp-phase lp-flat">
                <span className="lp-tag">01 · Sideways</span>
                <h3>Accumulation</h3>
                <p>Large operators quietly buy inside a range after a decline. The low gets
                   tested — think selling climax and spring.</p>
                <span className="lp-barline" />
              </div>
              <div className="lp-phase lp-up">
                <span className="lp-tag">02 · Up</span>
                <h3>Markup</h3>
                <p>Demand wins. Price breaks out of the range (sign of strength) and makes
                   higher highs and higher lows.</p>
                <span className="lp-barline" />
              </div>
              <div className="lp-phase lp-flat">
                <span className="lp-tag">03 · Sideways</span>
                <h3>Distribution</h3>
                <p>At the top the same operators sell back to latecomers. The range looks
                   strong, but the strength is leaking out.</p>
                <span className="lp-barline" />
              </div>
              <div className="lp-phase lp-down">
                <span className="lp-tag">04 · Down</span>
                <h3>Markdown</h3>
                <p>Supply wins. Price falls out of the range and makes lower lows. The cycle
                   is complete — and looks for a new bottom to begin again.</p>
                <span className="lp-barline" />
              </div>
            </div>

            <div>
              <svg className="lp-chart" ref={chartRef} viewBox="0 0 820 400" role="img"
                   aria-label="Wyckoff accumulation schematic: price falls into a selling climax, builds a range with a spring, and breaks out into markup, labelled phases A to E.">
                <g className="grid">
                  <line x1="0" y1="100" x2="820" y2="100" />
                  <line x1="0" y1="200" x2="820" y2="200" />
                  <line x1="0" y1="300" x2="820" y2="300" />
                </g>
                <line className="lvl res" x1="60" y1="150" x2="820" y2="150" />
                <line className="lvl sup" x1="60" y1="300" x2="820" y2="300" />
                <path className="price draw" d="M20,52 L110,150 L190,300 L280,150 L360,252 L430,196 L500,244 L560,318 L620,236 L700,120 L800,40" />
                <g className="fade" style={{ animationDelay: "1.5s" }}>
                  <circle className="evt" cx="190" cy="300" r="4" />
                  <text className="evt-lbl" x="176" y="322">SC</text>
                </g>
                <g className="fade" style={{ animationDelay: "1.7s" }}>
                  <circle className="evt" cx="280" cy="150" r="4" />
                  <text className="evt-lbl" x="268" y="140">AR</text>
                </g>
                <g className="fade" style={{ animationDelay: "1.9s" }}>
                  <circle className="evt" cx="360" cy="252" r="4" />
                  <text className="evt-lbl" x="348" y="274">ST</text>
                </g>
                <g className="fade" style={{ animationDelay: "2.1s" }}>
                  <circle className="evt-ring" cx="560" cy="318" r="6" />
                  <text className="evt-lbl" x="540" y="340">Spring</text>
                </g>
                <g className="fade" style={{ animationDelay: "2.3s" }}>
                  <circle className="evt" cx="700" cy="120" r="4" />
                  <text className="evt-lbl" x="690" y="110">SOS</text>
                </g>
                <g className="fade" style={{ animationDelay: "2.5s" }}>
                  <text className="phase" x="120" y="386">A</text>
                  <text className="phase" x="380" y="386">B</text>
                  <text className="phase" x="560" y="386">C</text>
                  <text className="phase" x="660" y="386">D</text>
                  <text className="phase" x="780" y="386">E</text>
                </g>
              </svg>
              <p className="lp-cap">Accumulation schematic — SC, AR, ST, Spring, SOS across phases A–E.</p>
            </div>
          </div>

          <p className="lp-composite">
            Wyckoff summed up those large operators in a single figure: the
            <b> Composite Man</b>. Picture one smart operator behind every move — buying low,
            letting the crowd chase the top, then selling. Your job isn't to beat him, but
            to read his footprints and move with them.
          </p>
        </div>
      </section>

      {/* TOOLS */}
      <section className="lp-section" id="tools">
        <div className="lp-wrap">
          <div className="lp-sec-head">
            <span className="lp-eyebrow">Get started</span>
            <h2>Four places to go next</h2>
            <p>From the community to the live chart and the daily scan — all under one roof.</p>
          </div>
          <div className="lp-tools">
            <a className="lp-tool" href={DISCORD_URL} target="_blank" rel="noopener noreferrer">
              <span className="lp-kicker">The community</span>
              <h3>Discord</h3>
              <p>The heart of the café. Analysis, signals, and traders learning together to
                 read what the market is doing.</p>
              <span className="lp-go">Come in</span>
            </a>
            {/* Dashboard opent de login binnen dezelfde app */}
            <button className="lp-tool" onClick={onEnter}>
              <span className="lp-kicker">Live chart</span>
              <h3>Trading&nbsp;Dashboard</h3>
              <p>The PCG interface with live candles, HTF context, zones, and Wyckoff signals
                 with entry, stop and target.</p>
              <span className="lp-go">Open the dashboard</span>
            </button>
            <a className="lp-tool" href={PNF_URL}>
              <span className="lp-kicker">Point &amp; Figure</span>
              <h3>PnF&nbsp;Terminal</h3>
              <p>Point &amp; Figure charts for crypto, indices and stocks, with ES view,
                 volume and target zones. Same login as the dashboard.</p>
              <span className="lp-go">Open the terminal</span>
            </a>
            <a className="lp-tool" href={RS_SCANNER_URL} target="_blank" rel="noopener noreferrer">
              <span className="lp-kicker">Daily scan</span>
              <h3>RS&nbsp;Scanner</h3>
              <p>Relative strength across hundreds of coins. See at a glance what's beating
                 the market and what's lagging.</p>
              <span className="lp-go">View the scan</span>
            </a>
          </div>
        </div>
      </section>

      {/* LEARN */}
      <section className="lp-section" id="learn">
        <div className="lp-wrap">
          <div className="lp-sec-head">
            <span className="lp-eyebrow">Learn</span>
            <h2>Wyckoff from foundation to execution</h2>
            <p>Two courses in the works: one to get started, one to truly master it.
               Coming soon.</p>
          </div>
          <div className="lp-courses">
            <div className="lp-course">
              <span className="lp-soon">Coming soon</span>
              <span className="lp-lvl">Level · Beginner</span>
              <h3>Wyckoff Basics</h3>
              <p>Learn to speak the market's language, step by step.</p>
              <ul>
                <li>The three laws in practice</li>
                <li>Spotting accumulation and distribution</li>
                <li>The events: SC, AR, ST, Spring, SOS</li>
                <li>Read your first range from start to finish</li>
              </ul>
            </div>
            <div className="lp-course">
              <span className="lp-soon">Coming soon</span>
              <span className="lp-lvl">Level · Advanced</span>
              <h3>Wyckoff Advanced</h3>
              <p>From recognising to trading, with real setups.</p>
              <ul>
                <li>Phase C entry: timing the spring and test</li>
                <li>Volume and effort-vs-result, read deep</li>
                <li>Building entry, stop and target around the range</li>
                <li>Live sessions and cases from the café</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <footer className="lp-footer">
        <div className="lp-wrap">
          <div className="lp-foot">
            <div className="lp-brand" style={{ fontSize: 18 }}>
              <img className="lp-logo" src="/wyckoffeelogo_socials.jpg" alt="Wyckoff Café logo" />
              Wyckoff&nbsp;Café
            </div>
            <div className="lp-mono">© 2026 · wycoffcafe.com</div>
          </div>
          <p className="lp-disclaimer">
            Educational content, not financial advice. Trading crypto and derivatives carries
            risk of loss. Make your own decisions.
          </p>
        </div>
      </footer>
    </div>
  );
}
